import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { financeAPI } from '../../services/api';
import { getErrorMessage } from '../../utils/errorHandler';

export const fetchFeeStructures = createAsyncThunk('finance/feeStructures', async (params, { rejectWithValue }) => {
  try {
    const response = await financeAPI.fetchFeeStructures(params);
    return response.data;
  } catch (error) {
    return rejectWithValue(getErrorMessage(error, 'Failed to fetch fee structures'));
  }
});

export const createFeeStructure = createAsyncThunk('finance/createFee', async (data, { rejectWithValue }) => {
  try {
    const response = await financeAPI.createFeeStructure(data);
    return response.data.data;
  } catch (error) {
    return rejectWithValue(getErrorMessage(error, 'Failed to create fee structure'));
  }
});

export const fetchStudentFees = createAsyncThunk('finance/studentFees', async (params, { rejectWithValue }) => {
  try {
    const response = await financeAPI.fetchStudentFees(params);
    return response.data;
  } catch (error) {
    return rejectWithValue(getErrorMessage(error, 'Failed to fetch student fees'));
  }
});

export const createPayment = createAsyncThunk('finance/createPayment', async (data, { rejectWithValue }) => {
  try {
    const response = await financeAPI.createPayment(data);
    return response.data.data;
  } catch (error) {
    return rejectWithValue(getErrorMessage(error, 'Failed to create payment'));
  }
});

export const fetchExpenses = createAsyncThunk('finance/expenses', async (params, { rejectWithValue }) => {
  try {
    const response = await financeAPI.fetchExpenses(params);
    return response.data;
  } catch (error) {
    return rejectWithValue(getErrorMessage(error, 'Failed to fetch expenses'));
  }
});

export const createExpense = createAsyncThunk('finance/createExpense', async (data, { rejectWithValue }) => {
  try {
    const response = await financeAPI.createExpense(data);
    return response.data.data;
  } catch (error) {
    return rejectWithValue(getErrorMessage(error, 'Failed to create expense'));
  }
});

export const fetchIncome = createAsyncThunk('finance/income', async (params, { rejectWithValue }) => {
  try {
    const response = await financeAPI.fetchIncome(params);
    return response.data;
  } catch (error) {
    return rejectWithValue(getErrorMessage(error, 'Failed to fetch income'));
  }
});

export const createIncome = createAsyncThunk('finance/createIncome', async (data, { rejectWithValue }) => {
  try {
    const response = await financeAPI.createIncome(data);
    return response.data.data;
  } catch (error) {
    return rejectWithValue(getErrorMessage(error, 'Failed to create income'));
  }
});

export const fetchReports = createAsyncThunk('finance/reports', async (params, { rejectWithValue }) => {
  try {
    const response = await financeAPI.fetchReports(params);
    return response.data;
  } catch (error) {
    return rejectWithValue(getErrorMessage(error, 'Failed to fetch reports'));
  }
});

export const fetchMonthlyReport = createAsyncThunk('finance/monthlyReport', async (params, { rejectWithValue }) => {
  try {
    const response = await financeAPI.getMonthlyReport(params);
    return response.data;
  } catch (error) {
    return rejectWithValue(getErrorMessage(error, 'Failed to fetch monthly report'));
  }
});

export const fetchStudentFeeProfile = createAsyncThunk('finance/studentFeeProfile', async (studentId, { rejectWithValue }) => {
  try {
    const response = await financeAPI.getStudentFeeProfile(studentId);
    return response.data;
  } catch (error) {
    return rejectWithValue(getErrorMessage(error, 'Failed to fetch student fee profile'));
  }
});

const initialState = {
  feeStructures: [],
  studentFees: [],
  payments: [],
  expenses: [],
  income: [],
  reports: null,
  dashboardStats: null,
  monthlyReport: null,
  studentFeeProfile: null,
  loading: false,
  error: null,
};

const financeSlice = createSlice({
  name: 'finance',
  initialState,
  reducers: {
    clearFinanceError: (state) => { state.error = null; },
    clearStudentFeeProfile: (state) => { state.studentFeeProfile = null; },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchFeeStructures.pending, (state) => { state.loading = true; })
      .addCase(fetchFeeStructures.fulfilled, (state, action) => { state.loading = false; state.feeStructures = action.payload.data || action.payload; })
      .addCase(fetchFeeStructures.rejected, (state, action) => { state.loading = false; state.error = action.payload; })
      .addCase(createFeeStructure.fulfilled, (state, action) => { state.feeStructures.unshift(action.payload); })
      .addCase(fetchStudentFees.fulfilled, (state, action) => { state.studentFees = action.payload.data || action.payload; })
      .addCase(createPayment.fulfilled, (state, action) => { state.payments.unshift(action.payload); })
      .addCase(fetchExpenses.fulfilled, (state, action) => { state.expenses = action.payload.data || action.payload; })
      .addCase(createExpense.fulfilled, (state, action) => { state.expenses.unshift(action.payload); })
      .addCase(fetchIncome.fulfilled, (state, action) => { state.income = action.payload.data || action.payload; })
      .addCase(createIncome.fulfilled, (state, action) => { state.income.unshift(action.payload); })
      .addCase(fetchReports.fulfilled, (state, action) => { state.reports = action.payload; })
      .addCase(fetchMonthlyReport.fulfilled, (state, action) => { state.monthlyReport = action.payload; })
      .addCase(fetchStudentFeeProfile.fulfilled, (state, action) => { state.studentFeeProfile = action.payload; });
  },
});

export const { clearFinanceError, clearStudentFeeProfile } = financeSlice.actions;
export default financeSlice.reducer;
